import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isRootNode,
  BLUR_COMMAND,
  COMMAND_PRIORITY_LOW,
  FOCUS_COMMAND,
} from 'lexical';

const ATTRIBUTE = 'data-placeholder';
const LEFT_FORMATS = new Set(['', 'left', 'start', 'justify']);

export default function BlockPlaceholderPlugin({ text = 'Type / for commands' }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    let current = null;

    const clear = () => {
      if (current) current.removeAttribute(ATTRIBUTE);
      current = null;
    };

    const apply = () => {
      const key = editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return null;
        const block = selection.anchor.getNode().getTopLevelElement();
        if (!$isParagraphNode(block) || !block.isEmpty()) return null;
        // Accordion content is a shadow root with its own placeholder.
        if (!$isRootNode(block.getParent())) return null;
        // The caret sits mid-line on centred and right-aligned blocks, on top of the hint.
        if (!LEFT_FORMATS.has(block.getFormatType())) return null;
        // An empty editor already shows the RichTextPlugin placeholder on this line.
        if ($getRoot().getChildrenSize() === 1) return null;
        return block.getKey();
      });
      const focused = editor.getRootElement() === document.activeElement;
      const element = key && focused ? editor.getElementByKey(key) : null;
      if (element === current) return;
      clear();
      if (element) {
        element.setAttribute(ATTRIBUTE, text);
        current = element;
      }
    };

    return mergeRegister(
      editor.registerUpdateListener(apply),
      editor.registerCommand(
        FOCUS_COMMAND,
        () => {
          apply();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          clear();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      clear,
    );
  }, [editor, text]);

  return null;
}
